import './BrowseTalent.css';
import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Breadcrumbs,
  Link,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  Avatar,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  Stack,
  InputAdornment,
  useTheme,
  alpha,
  Pagination
} from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import ArrowBack from '@mui/icons-material/ArrowBack';
import { styled } from '@mui/material/styles';
import {
  Search as SearchIcon,
  LocationOn as LocationOnIcon,
  AccessTime as AccessTimeIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { useSelector } from "react-redux";
import moment from 'moment';
import { useAuthToken } from "../TokenContext";
import { useIntl } from 'react-intl';
import { NavHashLink } from 'react-router-hash-link';
import { useHistory } from 'react-router-dom';

// Enhanced theme configuration (same as BrowseTalent)
const componentStyles = {
  talentTypeChip: {
    PARTNER: theme => ({
      backgroundColor: theme.palette.mode === 'dark' 
        ? 'rgba(16, 185, 129, 0.2)' 
        : 'rgba(16, 185, 129, 0.1)',
      color: theme.palette.mode === 'dark' ? '#34D399' : '#059669',
      borderColor: '#10B981',
      fontWeight: 500
    }),
    FREELANCER: theme => ({
      backgroundColor: theme.palette.mode === 'dark'
        ? 'rgba(245, 158, 11, 0.2)'
        : 'rgba(245, 158, 11, 0.1)',
      color: theme.palette.mode === 'dark' ? '#FBBF24' : '#D97706',
      borderColor: '#F59E0B',
      fontWeight: 500
    }),
    CLIENT: theme => ({
      backgroundColor: theme.palette.mode === 'dark'
        ? 'rgba(59, 130, 246, 0.2)'
        : 'rgba(59, 130, 246, 0.1)',
      color: theme.palette.mode === 'dark' ? '#60A5FA' : '#2563EB',
      borderColor: '#3B82F6',
      fontWeight: 500
    })
  },

  availabilityChip: {
    available: theme => ({
      backgroundColor: theme.palette.mode === 'dark'
        ? 'rgba(16, 185, 129, 0.2)'
        : 'rgba(16, 185, 129, 0.1)',
      color: theme.palette.mode === 'dark' ? '#34D399' : '#059669',
      borderColor: theme.palette.mode === 'dark' ? '#34D399' : '#10B981',
      fontWeight: 500
    }),
    unavailable: theme => ({
      backgroundColor: theme.palette.mode === 'dark'
        ? 'rgba(239, 68, 68, 0.2)'
        : 'rgba(239, 68, 68, 0.1)',
      color: theme.palette.mode === 'dark' ? '#FCA5A5' : '#DC2626',
      borderColor: theme.palette.mode === 'dark' ? '#FCA5A5' : '#EF4444',
      fontWeight: 500
    })
  },

  talentCard: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    borderRadius: '16px',
    overflow: 'hidden',
    backgroundColor: theme => theme.palette.mode === 'dark' ? '#2d2d2d' : '#FFFFFF',
    boxShadow: theme => theme.palette.mode === 'dark'
      ? '0px 4px 20px rgba(0, 0, 0, 0.2)'
      : '0px 4px 20px rgba(34, 41, 47, 0.1)',
    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: theme => theme.palette.mode === 'dark'
        ? '0px 12px 24px rgba(0, 0, 0, 0.3)'
        : '0px 12px 24px rgba(34, 41, 47, 0.15)'
    }
  },

  searchBar: {
    background: theme => theme.palette.mode === 'dark' ? '#2d2d2d' : '#FFFFFF',
    borderRadius: '12px',
    boxShadow: theme => theme.palette.mode === 'dark'
      ? '0px 4px 12px rgba(0, 0, 0, 0.2)'
      : '0px 4px 12px rgba(0, 0, 0, 0.05)',
    padding: '16px 24px',
    border: theme => `1px solid ${theme.palette.mode === 'dark' 
      ? 'rgba(255,255,255,0.1)' 
      : 'rgba(229, 231, 235, 0.5)'}`
  },

  filterSection: {
    backgroundColor: theme => theme.palette.mode === 'dark' ? '#2d2d2d' : '#FFFFFF',
    borderRadius: '16px',
    padding: '24px',
    boxShadow: theme => theme.palette.mode === 'dark'
      ? '0px 4px 16px rgba(0, 0, 0, 0.2)'
      : '0px 4px 16px rgba(0, 0, 0, 0.06)',
    border: theme => `1px solid ${theme.palette.mode === 'dark' 
      ? 'rgba(255,255,255,0.1)' 
      : 'rgba(229, 231, 235, 0.5)'}`
  },

  select: {
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px',
      backgroundColor: theme => theme.palette.mode === 'dark' ? '#1e1e1e' : '#ffffff',
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: '#4F46E5',
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: '#4F46E5',
      }
    }
  },

  primaryButton: {
    background: 'linear-gradient(135deg, #4F46E5 0%, #3730A3 100%)',
    borderRadius: '10px',
    textTransform: 'none',
    fontWeight: 500,
    padding: '10px 24px',
    boxShadow: '0px 4px 12px rgba(79, 70, 229, 0.2)',
    '&:hover': {
      background: 'linear-gradient(135deg, #4338CA 0%, #312E81 100%)',
      boxShadow: '0px 6px 16px rgba(79, 70, 229, 0.3)'
    }
  },

  skillChip: {
    backgroundColor: theme => theme.palette.mode === 'dark'
      ? 'rgba(79, 70, 229, 0.2)'
      : 'rgba(79, 70, 229, 0.08)',
    color: theme => theme.palette.mode === 'dark' ? '#818CF8' : '#4F46E5',
    borderRadius: '8px',
    fontWeight: 500,
    border: 'none',
    '&:hover': {
      backgroundColor: theme => theme.palette.mode === 'dark'
        ? 'rgba(79, 70, 229, 0.3)'
        : 'rgba(79, 70, 229, 0.12)'
    }
  }
};

// Styled Components
const StyledContainer = styled(Container)(({ theme }) => ({
  width: 'auto',
  maxWidth: '100%',
 
  marginRight: theme.spacing(2),
  marginBottom: theme.spacing(4),
  background: theme.palette.mode === 'dark' 
    ? 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)'
    : 'linear-gradient(135deg, #f6f9fc 0%, #eef2f7 100%)',
  '@media (max-width: 1200px)': {
    
    padding: theme.spacing(2),
  },
  '@media (max-width: 900px)': {
   
    padding: theme.spacing(1.5),
  },
  '@media (max-width: 600px)': {
  
    padding: theme.spacing(1),
    marginRight: theme.spacing(1),
  }
}));

const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'all 0.3s ease-in-out',
  backgroundColor: theme.palette.mode === 'dark' ? '#2d2d2d' : '#ffffff',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.palette.mode === 'dark' 
      ? '0 8px 24px rgba(0,0,0,0.4)' 
      : theme.shadows[8]
  },
  '& .MuiCardContent-root': {
    backgroundColor: theme.palette.mode === 'dark' ? '#2d2d2d' : '#ffffff',
  },
  '& .MuiTypography-root': {
    color: theme.palette.mode === 'dark' ? '#fff' : 'inherit'
  },
  '& .MuiChip-root': {
    backgroundColor: theme.palette.mode === 'dark' 
      ? 'rgba(255,255,255,0.1)' 
      : 'rgba(0,0,0,0.08)',
    color: theme.palette.mode === 'dark' ? '#fff' : 'inherit'
  }
}));

const SearchBar = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  backgroundColor: theme.palette.mode === 'dark' ? '#2d2d2d' : theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: theme.palette.mode === 'dark'
    ? '0 4px 20px rgba(0,0,0,0.2)'
    : `0 4px 20px ${alpha(theme.palette.common.black, 0.1)}`,
  marginBottom: theme.spacing(4),
  '& .MuiInputBase-root': {
    backgroundColor: theme.palette.mode === 'dark' ? '#1e1e1e' : '#ffffff',
    '&:hover': {
      backgroundColor: theme.palette.mode === 'dark' ? '#252525' : '#f8fafc',
    },
    '&.Mui-focused': {
      backgroundColor: theme.palette.mode === 'dark' ? '#2d2d2d' : '#ffffff',
    }
  }
}));

const FilterGrid = styled(Grid)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? '#2d2d2d' : theme.palette.background.paper,
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadius,
  marginBottom: theme.spacing(4),
  '& .MuiFormLabel-root': {
    color: theme.palette.mode === 'dark' ? '#fff' : 'inherit'
  },
  '& .MuiSelect-select': {
    backgroundColor: theme.palette.mode === 'dark' ? '#1e1e1e' : '#ffffff',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.mode === 'dark' 
      ? 'rgba(255,255,255,0.2)' 
      : 'rgba(0,0,0,0.1)',
  }
}));

const TALENT_TYPE_LABELS = {
  PARTNER: "P",
  FREELANCER: "F",
  CLIENT: "I"
};

const FindTalentPartnerGuest = () => {
  const theme = useTheme();
  const token = useAuthToken();
  const intl = useIntl();
  const history = useHistory();
  const { user } = useSelector((state) => state.auth);

  // State Management
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [talents, setTalents] = useState([]);
  const [filteredTalents, setFilteredTalents] = useState([]);
  const [searchFilter, setSearchFilter] = useState('');

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(8);

  // Filter States
  const [filters, setFilters] = useState({
    primarySkill: 'default',
    secondarySkill: 'default',
    availability: 'default',
    timezone: 'default',
    workHours: 'default',
    jobTitle: 'default',
    location: 'default',
    budget: 'default'
  });

  // Initial Data Fetch
  useEffect(() => {
    if (user?.data?.[0]?.partner_code && token) {
      searchTalents();
    }
  }, [token, user]);

  // Search and Filter Functions
  const searchTalents = async () => {
    try {
      setLoading(true);
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      };

      const requestBody = {
        project_location: filters.location !== 'default' ? filters.location : '',
        project_name: filters.jobTitle !== 'default' ? filters.jobTitle : '',
        budget: filters.budget !== 'default' ? filters.budget : '',
        primaryskill: filters.primarySkill !== 'default' ? filters.primarySkill : '',
        secondaryskill: filters.secondarySkill !== 'default' ? filters.secondarySkill : '',
        timezone: filters.timezone !== 'default' ? filters.timezone : '',
        availability: filters.availability !== 'default' ? filters.availability : '',
        workhours: filters.workHours !== 'default' ? filters.workHours : '',
        about: '',
        partner_code: user.data[0].partner_code,
      };

      const response = await fetch(
        process.env.REACT_APP_PARTNER_PPODPARTNERGUESTFINDTALENT,
        {
          method: 'POST',
          headers,
          body: JSON.stringify(requestBody)
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Something went wrong');
      }

      const data = await response.json();
      setTalents(data.data);
      setFilteredTalents(data.data);
    } catch (error) {
      console.error('Search error:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (name, value) => {
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const resetFilters = () => {
    const resetFilterState = {
      primarySkill: 'default',
      secondarySkill: 'default',
      availability: 'default',
      timezone: 'default',
      workHours: 'default',
      jobTitle: 'default',
      location: 'default',
      budget: 'default'
    };
    
    setFilters(resetFilterState);
    setSearchFilter('');
    setCurrentPage(1);
  };

  const handleTextSearch = (event) => {
    const searchTerm = event.target.value.toLowerCase();
    setSearchFilter(searchTerm);

    if (!searchTerm) {
      setFilteredTalents(talents);
    } else {
      const filtered = talents.filter(item => 
        item.firstName.toLowerCase().includes(searchTerm) ||
        item.primaryskill.toLowerCase().includes(searchTerm) ||
        item.secondaryskill.toLowerCase().includes(searchTerm)
      );
      setFilteredTalents(filtered);
    }
    setCurrentPage(1);
  };

  // Pagination Logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredTalents.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredTalents.length / itemsPerPage);

  const handlePageChange = (event, pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Render Functions
  const renderTalentCard = (talent) => {
    const getTalentTypeStyles = (talentType) => {
      switch(talentType) {
        case 'PARTNER':
          return componentStyles.talentTypeChip.PARTNER(theme);
        case 'FREELANCER':
          return componentStyles.talentTypeChip.FREELANCER(theme);
        case 'CLIENT':
          return componentStyles.talentTypeChip.CLIENT(theme);
        default:
          return componentStyles.talentTypeChip.FREELANCER(theme);
      }
    };

    const getAvailabilityStyles = (status) => {
      return status === "Yes" 
        ? componentStyles.availabilityChip.available(theme)
        : componentStyles.availabilityChip.unavailable(theme);
    };

    return (
      <StyledCard sx={componentStyles.talentCard}>
        <CardContent>
          <Stack spacing={2}>
            {/* Profile Header */}
            <Stack direction="row" spacing={2} alignItems="center">
              <Avatar
                src={talent.profilePicture ? 
                  `https://globldataprod.s3.amazonaws.com/${talent.profilePicture}` : 
                  '/defaultimg.png'
                }
                sx={{ width: 60, height: 60 }}
              />
              <Box>
                <Stack direction="row" spacing={1} alignItems="center">
                  <NavHashLink 
                    to={{ pathname: '/Candidatedetailsbyid', state: { c_id: talent.c_id } }}
                    style={{ textDecoration: 'none' }}
                  >
                    <Typography variant="h6" color="primary">
                      {talent.firstName}
                    </Typography>
                  </NavHashLink>
                  <Chip
                    size="small"
                    label={TALENT_TYPE_LABELS[talent.talent_type] || "F"}
                    sx={getTalentTypeStyles(talent.talent_type)}
                  />
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  {talent.work_position}
                </Typography>
              </Box>
            </Stack>

            {/* Location & Skills */}
            <Stack spacing={1}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LocationOnIcon fontSize="small" color="action" />
                <Typography variant="body2">{talent.country}</Typography>
              </Box>
              
              <Stack direction="row" spacing={1}>
                <Chip size="small" label={talent.primaryskill} sx={componentStyles.skillChip} variant="outlined" />
                <Chip size="small" label={talent.secondaryskill} sx={componentStyles.skillChip} variant="outlined" />
              </Stack>

              <Typography variant="body2" color="text.secondary">
                {talent.about}
              </Typography>
            </Stack>

            {/* Availability Status */}
            <Stack spacing={1}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AccessTimeIcon fontSize="small" color="action" />
                <Typography variant="body2">
                  {talent.timezone} • {talent.availability}
                </Typography>
              </Box>
              
              {talent.currentStatus === "Yes" ? (
                <Chip
                  size="small"
                  label={intl.formatMessage({ id: 'currentlyAvailable' }) || 'Currently Available'}
                  sx={getAvailabilityStyles("Yes")}
                  variant="outlined"
                />
              ) : (
                <Stack spacing={0.5}>
                  <Chip
                    size="small"
                    label={intl.formatMessage({ id: 'currentlyUnavailable' }) || 'Currently Unavailable'}
                    sx={getAvailabilityStyles("No")}
                    variant="outlined"
                  />
                  {talent.availabledate && (
                    <Typography variant="caption" color="error">
                      {intl.formatMessage({ id: 'availableFrom' }) || 'Available from'}: 
                      {moment(talent.availabledate).format("YYYY-MM-DD")}
                    </Typography>
                  )}
                </Stack>
              )}
            </Stack>
          </Stack>
        </CardContent>
      </StyledCard>
    );
  };

  // Filter Options Constants
  const PRIMARY_SKILLS = [
    { value: 'html', label: 'HTML' },
    { value: 'css', label: 'CSS' },
    { value: 'JavaScript', label: 'JavaScript' },
    { value: 'Adobe Illustrator', label: 'Adobe Illustrator' },
    { value: 'ReactJS', label: 'ReactJS' },
    { value: 'AngularJS', label: 'AngularJS' },
    { value: 'Java', label: 'Java' },
    { value: 'Node.js', label: 'Node.js' },
    { value: 'Vue', label: 'Vue' },
    { value: 'Next', label: 'Next' },
    { value: 'Typescript', label: 'Typescript' },
    { value: 'Gatsby', label: 'Gatsby' },
    { value: 'Three.js', label: 'Three.js' },
    { value: 'Tableau', label: 'Tableau' },
    { value: 'Python', label: 'Python' },
    { value: 'Quality Assurance', label: 'Quality Assurance' },
    { value: 'IOS Development (Swift/Objective C)', label: 'IOS Development' },
    { value: 'Android Development (Java/Kotlin)', label: 'Android Development' },
    { value: 'Devops', label: 'Devops' },
    { value: 'Cloud (AWS/Azure/GCP)', label: 'Cloud' }
  ];

  const SECONDARY_SKILLS = [
    { value: 'Python', label: 'Python' },
    { value: 'Java', label: 'Java' },
    { value: 'PHP', label: 'PHP' },
    { value: 'SQL', label: 'SQL' },
    { value: 'Git', label: 'Git' },
    { value: 'c++', label: 'C++' },
    { value: 'Perl', label: 'Perl' },
    { value: 'AngularJS', label: 'AngularJS' },
    { value: 'Kotlin', label: 'Kotlin' },
    { value: 'AWS', label: 'AWS' },
    { value: 'NodeJs', label: 'NodeJs' },
    { value: 'Azure', label: 'Azure' }
  ];

  const TIMEZONES = [
    { value: 'indian_standard_time', label: 'Indian Standard Time' },
    { value: 'eastern_standard_time', label: 'Eastern Standard Time' },
    { value: 'central_standard_time', label: 'Central Standard Time' },
    { value: 'mountain_standard_time', label: 'Mountain Standard Time' },
    { value: 'pacific_standard_time', label: 'Pacific Standard Time' },
    { value: 'greenwich_mean_time', label: 'Greenwich Mean Time' },
    { value: 'central_european_time', label: 'Central European Time' },
    { value: 'western_european_time', label: 'Western European Time' },
    // Add more timezones as needed
  ];

  const LOCATIONS = [
    { value: 'usa', label: 'USA' },
    { value: 'india', label: 'India' },
    { value: 'canada', label: 'Canada' },
    { value: 'australia', label: 'Australia' },
    { value: 'uk', label: 'United Kingdom' },
    { value: 'germany', label: 'Germany' },
    { value: 'france', label: 'France' },
    // Add more locations as needed
  ];

  // Main Render
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <StyledContainer maxWidth="xl" >
      <Breadcrumbs
        separator={<NavigateNextIcon fontSize="small" />}
        aria-label="breadcrumb"
        sx={{ mb: 2 }}
      >
        <Link
          component="button"
          onClick={() => history.goBack()}
          underline="hover"
          sx={{
            color: 'text.secondary',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <ArrowBack sx={{ mr: 0.5 }} fontSize="small" />
          {intl.formatMessage({ id: 'back' }) || 'Back'}
        </Link>
        <Typography color="text.primary">
          {intl.formatMessage({ id: 'browseTalent' }) || 'Browse Talent'}
        </Typography>
      </Breadcrumbs>

      {/* Header Section */}
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 600 }}>
        {intl.formatMessage({ id: 'browseTalent' }) || 'Browse Talent'}
      </Typography>
      
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        {intl.formatMessage({ id: 'findBestFreelancers' }) || 'Find the best freelancers and partners for your projects'}
      </Typography>

      {/* Search Bar */}
      <SearchBar sx={componentStyles.searchBar}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              placeholder={intl.formatMessage({ id: 'keyword' }) || 'Search by keyword...'}
              value={searchFilter}
              onChange={handleTextSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                )
              }}
            />
          </Grid>
        </Grid>
      </SearchBar>

      {/* Filters Section */}
      <FilterGrid container spacing={2} sx={componentStyles.filterSection}>
        {/* Primary Skill */}
        <Grid item xs={12} md={6} lg={3}>
          <FormControl fullWidth>
            <InputLabel>Primary Skill</InputLabel>
            <Select
              value={filters.primarySkill}
              onChange={(e) => handleFilterChange('primarySkill', e.target.value)}
              label="Primary Skill"
              sx={componentStyles.select}
            >
              <MenuItem value="default">Select Skill</MenuItem>
              {PRIMARY_SKILLS.map((skill) => (
                <MenuItem key={skill.value} value={skill.value}>
                  {skill.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Secondary Skill */}
        <Grid item xs={12} md={6} lg={3}>
          <FormControl fullWidth>
            <InputLabel>Secondary Skill</InputLabel>
            <Select
              value={filters.secondarySkill}
              onChange={(e) => handleFilterChange('secondarySkill', e.target.value)}
              label="Secondary Skill"
              sx={componentStyles.select}
            >
              <MenuItem value="default">Select Skill</MenuItem>
              {SECONDARY_SKILLS.map((skill) => (
                <MenuItem key={skill.value} value={skill.value}>
                  {skill.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Availability */}
        <Grid item xs={12} md={6} lg={3}>
          <FormControl fullWidth>
            <InputLabel>Availability</InputLabel>
            <Select
              value={filters.availability}
              onChange={(e) => handleFilterChange('availability', e.target.value)}
              label="Availability"
              sx={componentStyles.select}
            >
              <MenuItem value="default">Select Availability</MenuItem>
              <MenuItem value="Part time">Part Time</MenuItem>
              <MenuItem value="Full time">Full Time</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {/* Timezone */}
        <Grid item xs={12} md={6} lg={3}>
          <FormControl fullWidth>
            <InputLabel>Time Zone</InputLabel>
            <Select
              value={filters.timezone}
              onChange={(e) => handleFilterChange('timezone', e.target.value)}
              label="Time Zone"
              sx={componentStyles.select}
            >
              <MenuItem value="default">Select Timezone</MenuItem>
              {TIMEZONES.map((timezone) => (
                <MenuItem key={timezone.value} value={timezone.value}>
                  {timezone.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Work Hours */}
        <Grid item xs={12} md={6} lg={3}>
          <FormControl fullWidth>
            <InputLabel>Preferred Work Hours</InputLabel>
            <Select
              value={filters.workHours}
              onChange={(e) => handleFilterChange('workHours', e.target.value)}
              label="Preferred Work Hours"
              sx={componentStyles.select}
            >
              <MenuItem value="default">Select Work Hours</MenuItem>
              <MenuItem value="Morning Shift, 9am - 6pm">Morning Shift, 9am - 6pm</MenuItem>
              <MenuItem value="Night Shift, 10pm - 7am">Night Shift, 10pm - 7am</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {/* Job Title */}
        <Grid item xs={12} md={6} lg={3}>
          <FormControl fullWidth>
            <InputLabel>Job Title</InputLabel>
            <Select
              value={filters.jobTitle}
              onChange={(e) => handleFilterChange('jobTitle', e.target.value)}
              label="Job Title"
              sx={componentStyles.select}
            >
              <MenuItem value="default">Select Job Title</MenuItem>
              <MenuItem value="Software Engineer">Software Engineer</MenuItem>
              <MenuItem value="Full Stack Developer">Full Stack Developer</MenuItem>
              <MenuItem value="Java Developer">Java Developer</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {/* Location */}
        <Grid item xs={12} md={6} lg={3}>
          <FormControl fullWidth>
            <InputLabel>Location</InputLabel>
            <Select
              value={filters.location}
              onChange={(e) => handleFilterChange('location', e.target.value)}
              label="Location"
              sx={componentStyles.select}
            >
              <MenuItem value="default">Select Location</MenuItem>
              {LOCATIONS.map((location) => (
                <MenuItem key={location.value} value={location.value}>
                  {location.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Filter Actions */}
        <Grid item xs={12}>
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button
              variant="outlined"
              startIcon={<ClearIcon />}
              onClick={resetFilters}
            >
              {intl.formatMessage({ id: 'clear' }) || 'Clear'}
            </Button>
            <Button
              variant="contained"
              onClick={searchTalents}
              sx={componentStyles.primaryButton}
            >
              {intl.formatMessage({ id: 'applyFilters' }) || 'Apply Filters'}
            </Button>
          </Stack>
        </Grid>
      </FilterGrid>

      {/* Results Count */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {filteredTalents.length} {intl.formatMessage({ id: 'candidates' }) || 'candidates'} found
      </Typography>

      {/* Talents Grid */}
      <Grid container spacing={3}>
        {currentItems.map((talent, index) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={`${talent.c_id}-${index}`}>
            {renderTalentCard(talent)}
          </Grid>
        ))}
      </Grid>

      {/* No Results */}
      {filteredTalents.length === 0 && !loading && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" color="text.secondary">
            No talents found matching your criteria
          </Typography>
        </Box>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <Stack
          direction="row"
          spacing={2}
          justifyContent="center"
          sx={{ mt: 4 }}
        >
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
            size="large"
            showFirstButton
            showLastButton
          />
        </Stack>
      )}

      {/* Error Alert */}
      {error && (
        <Alert
          severity="error"
          onClose={() => setError(null)}
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            zIndex: theme.zIndex.snackbar
          }}
        >
          {error}
        </Alert>
      )}
    </StyledContainer>
  );
};

export default FindTalentPartnerGuest;